import { Check, SearchMd } from "@untitledui/icons";
import { type ReactNode, useState } from "react";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Input } from "@/components/base/input/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export type CohortOption = { id: string; name: string; color: string; assignedCount: number };

/** Only worth a search field once the list is long enough to scan poorly. */
const SEARCH_THRESHOLD = 6;

export const CohortDot = ({ color, className = "size-2" }: { color: string; className?: string }) => (
  <span aria-hidden className={`shrink-0 rounded-full ${className}`} style={{ backgroundColor: color }} />
);

export interface CohortSelectPanelProps {
  /** Selectable rows. */
  options: CohortOption[];
  /** Pre-ticked on open — used when editing an existing selection. */
  initialSelected?: string[];
  optionsLabel?: string;
  emptyLabel?: string;
  confirmLabel?: string;
  onConfirm: (cohortIds: string[]) => void;
}

/**
 * Multi-select cohort picker. Nothing commits until the footer action, so the panel stays open
 * while several cohorts are ticked.
 */
export function CohortSelectPanel({
  options,
  initialSelected = [],
  optionsLabel = "Cohorts",
  emptyLabel = "Every cohort is already spoken for",
  confirmLabel = "Save",
  onConfirm,
}: CohortSelectPanelProps) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<string[]>(initialSelected);

  const toggle = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((n) => n !== id) : [...prev, id]));

  const q = query.trim().toLowerCase();
  const shown = q ? options.filter((c) => c.name.toLowerCase().includes(q)) : options;

  return (
    <div className="flex w-[300px] flex-col py-1">
      {options.length > SEARCH_THRESHOLD && (
        <div className="px-3 py-2">
          <Input autoFocus size="sm" value={query} onChange={setQuery} placeholder="Find a cohort…" icon={SearchMd} />
        </div>
      )}

      <div className="max-h-[320px] overflow-y-auto">
        <p className="px-3 py-1.5 text-[11px] font-semibold tracking-wide text-quaternary uppercase">{optionsLabel}</p>
        {shown.length > 0 ? (
          shown.map((cohort) => (
            <button
              key={cohort.id}
              type="button"
              onClick={() => toggle(cohort.id)}
              className="flex w-full cursor-pointer items-center gap-2.5 px-3 py-2 text-left text-sm font-medium text-secondary transition-colors duration-100 hover:bg-secondary"
            >
              <Checkbox isSelected={selected.includes(cohort.id)} size="sm" />
              <CohortDot color={cohort.color} />
              <span className="min-w-0 flex-1 truncate">{cohort.name}</span>
              <span className="shrink-0 text-xs text-quaternary">{cohort.assignedCount.toLocaleString("en-US")}</span>
              {selected.includes(cohort.id) && <Check className="size-4 shrink-0" />}
            </button>
          ))
        ) : (
          <p className="px-3 py-2 text-sm text-tertiary">{q ? `No cohorts match “${query.trim()}”` : emptyLabel}</p>
        )}
      </div>

      {options.length > 0 && (
        <div className="mt-1 border-t border-secondary px-3 pt-2 pb-1">
          <Button
            size="sm"
            className="w-full justify-center"
            isDisabled={selected.length === 0 && initialSelected.length === 0}
            onClick={() => onConfirm(selected)}
          >
            {confirmLabel}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * The panel in a popover that closes on confirm. The panel commits a whole selection at once, so
 * leaving the popover open after Save would strand the user in a picker that no longer has
 * anything to apply.
 */
export function CohortSelectPopover({
  trigger,
  align = "start",
  open: controlledOpen,
  onOpenChange,
  ...panel
}: CohortSelectPanelProps & {
  trigger: ReactNode;
  align?: "start" | "end";
  /** Supply with `onOpenChange` to open the picker from somewhere other than its own trigger. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = (next: boolean) => {
    setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align={align} className="w-auto p-0">
        {/* Remounted per opening so a cancelled edit doesn't leave stale ticks behind. */}
        {open && (
          <CohortSelectPanel
            {...panel}
            onConfirm={(ids) => {
              panel.onConfirm(ids);
              setOpen(false);
            }}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}
